import React from "react";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  EmptyState,
} from "@/components/ui";
import { MappingTableRow } from "@/components/catalog/MappingTableRow";
import type { MappingReviewMappingsTableProps } from "./MappingReviewMappingsTable.types";

function getVisiblePages(page: number, totalPages: number): number[] {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export const MappingReviewMappingsTable: React.FC<
  MappingReviewMappingsTableProps
> = ({
  mappings,
  allCount,
  isLoading = false,
  selectedMappingId,
  onSelectMapping,
  onResetFilters,
  pageInfo,
  onPageChange,
}) => {
  const showingStart =
    pageInfo.total === 0 ? 0 : (pageInfo.page - 1) * pageInfo.pageSize + 1;
  const showingEnd = Math.min(
    pageInfo.page * pageInfo.pageSize,
    pageInfo.total,
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Marketplace Mapping Sessions
            <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
              ({mappings.length} of {allCount})
            </span>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              Loading mapping sessions...
            </div>
          </div>
        ) : mappings.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No mappings found"
            description="Try adjusting your filters or search query"
            action={
              <Button variant="outline" onClick={onResetFilters}>
                Reset Filters
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/60 border-y border-slate-200 dark:border-slate-700">
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Product Type
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Canonical Category
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Mapped At
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Mapped By
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping) => (
                  <MappingTableRow
                    key={mapping.id}
                    mapping={mapping}
                    isSelected={mapping.id === selectedMappingId}
                    onSelect={onSelectMapping}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {!isLoading && pageInfo.totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Showing {showingStart}-{showingEnd} of {pageInfo.total}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={!pageInfo.hasPreviousPage}
              onClick={() => onPageChange(pageInfo.page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {getVisiblePages(pageInfo.page, pageInfo.totalPages).map((page) => (
              <Button
                key={page}
                variant={page === pageInfo.page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className={page === pageInfo.page ? "pointer-events-none" : ""}
              >
                {page}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              disabled={!pageInfo.hasNextPage}
              onClick={() => onPageChange(pageInfo.page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
